import React, { useEffect, useState } from 'react';
import {
  Activity, RefreshCw, Zap, Database, Clock, CheckCircle, TrendingUp,
  Globe, AlertTriangle, AlertCircle, Loader2,
} from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_COLORS = {
  polymarket: 'bg-purple-500',
  metaculus: 'bg-blue-500',
  predictit: 'bg-green-500',
  manifold: 'bg-orange-500',
  news_trends: 'bg-cyan-500',
};

const ScraperHealthWidget = () => {
  const [scraperStatus, setScraperStatus] = useState(null);
  const [scraperHistory, setScraperHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [triggering, setTriggering] = useState(false);

  const fetchScraperStatus = async () => {
    try {
      const [statusRes, historyRes] = await Promise.all([
        fetch(`${API_URL}/api/scraper/status`),
        fetch(`${API_URL}/api/scraper/history?limit=20`),
      ]);
      const statusData = await statusRes.json();
      const historyData = await historyRes.json();
      setScraperStatus(statusData);
      setScraperHistory(historyData.history || []);
    } catch (error) {
      console.error('Failed to fetch scraper status:', error);
    } finally {
      setLoading(false);
    }
  };

  const triggerManualScrape = async () => {
    setTriggering(true);
    try {
      await fetch(`${API_URL}/api/scraper/run`);
      await new Promise((resolve) => setTimeout(resolve, 2000));
      await fetchScraperStatus();
    } catch (error) {
      console.error('Failed to trigger scrape:', error);
    } finally {
      setTriggering(false);
    }
  };

  const refreshStatus = async () => {
    setRefreshing(true);
    await fetchScraperStatus();
    setRefreshing(false);
  };

  useEffect(() => {
    fetchScraperStatus();
    const interval = setInterval(fetchScraperStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 text-[#3B82F6] animate-spin" />
          <span className="ml-2 text-gray-400">Loading scraper status...</span>
        </div>
      </div>
    );
  }

  const totalPredictions = scraperStatus?.cached_predictions?.total || 0;
  const bySource = scraperStatus?.cached_predictions?.by_source || {};
  const maxCount = Math.max(...scraperHistory.map((h) => h.total_count || 0), 1);

  return (
    <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <Activity className="h-5 w-5 text-[#3B82F6]" />
          Scraper Health & Statistics
        </h2>
        <div className="flex gap-2">
          <Button
            onClick={refreshStatus}
            disabled={refreshing}
            size="sm"
            variant="outline"
            className="border-[#1e2a45] text-gray-300 hover:bg-[#1e2a45]"
            data-testid="refresh-scraper-status"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={triggerManualScrape}
            disabled={triggering}
            size="sm"
            className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
            data-testid="trigger-scraper"
          >
            {triggering ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}
            {triggering ? 'Scraping...' : 'Run Now'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-[#1e2a45]/50 rounded-xl p-4 border border-[#1e2a45]">
          <div className="flex items-center gap-2 mb-3">
            <div className={`w-3 h-3 rounded-full ${scraperStatus?.status === 'running' ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
            <span className="text-sm font-medium text-gray-300">Status</span>
          </div>
          <p className={`text-xl font-bold ${scraperStatus?.status === 'running' ? 'text-green-400' : 'text-red-400'}`}>
            {scraperStatus?.status === 'running' ? 'RUNNING' : 'STOPPED'}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Failures: {scraperStatus?.consecutive_failures || 0}/{scraperStatus?.alert_threshold || 3}
          </p>
        </div>

        <div className="bg-[#1e2a45]/50 rounded-xl p-4 border border-[#1e2a45]">
          <div className="flex items-center gap-2 mb-3">
            <Database className="h-5 w-5 text-[#3B82F6]" />
            <span className="text-sm font-medium text-gray-300">Cached</span>
          </div>
          <p className="text-3xl font-bold text-white">{totalPredictions.toLocaleString()}</p>
          <p className="text-xs text-gray-500 mt-1">{Object.keys(bySource).length} sources</p>
        </div>

        <div className="bg-[#1e2a45]/50 rounded-xl p-4 border border-[#1e2a45]">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-5 w-5 text-amber-400" />
            <span className="text-sm font-medium text-gray-300">Next Scrape</span>
          </div>
          <p className="text-xl font-bold text-amber-400">
            {scraperStatus?.time_until_next_scrape || 'Starting...'}
          </p>
          <p className="text-xs text-gray-500 mt-1">Interval: 24 hours</p>
        </div>

        <div className="bg-[#1e2a45]/50 rounded-xl p-4 border border-[#1e2a45]">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle className="h-5 w-5 text-green-400" />
            <span className="text-sm font-medium text-gray-300">Success Rate</span>
          </div>
          <p className="text-3xl font-bold text-green-400">
            {scraperHistory.length > 0
              ? Math.round((scraperHistory.filter((h) => h.success).length / scraperHistory.length) * 100)
              : 100}%
          </p>
          <p className="text-xs text-gray-500 mt-1">Last {scraperHistory.length} runs</p>
        </div>
      </div>

      {scraperHistory.length > 0 && (
        <div className="bg-[#1e2a45]/30 rounded-xl p-4 border border-[#1e2a45] mb-6">
          <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-green-400" />
            Scraper Performance History
          </h3>
          <div className="flex items-end gap-1 h-32">
            {scraperHistory.map((entry, idx) => {
              const height = (entry.total_count / maxCount) * 100;
              const isSuccess = entry.success !== false;
              return (
                <div
                  key={entry.timestamp || `history-${idx}`}
                  className="flex-1 group relative"
                  title={`${new Date(entry.timestamp).toLocaleDateString()}: ${entry.total_count} predictions`}
                >
                  <div
                    className={`w-full rounded-t transition-all ${isSuccess ? 'bg-[#3B82F6] hover:bg-[#60A5FA]' : 'bg-red-500 hover:bg-red-400'}`}
                    style={{ height: `${Math.max(height, 5)}%` }}
                  />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-[#050810] border border-[#1e2a45] rounded text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                    {new Date(entry.timestamp).toLocaleDateString()} - {entry.total_count} predictions
                    {!isSuccess && <span className="text-red-400 ml-1">(failed)</span>}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-2">
            <span>Older</span>
            <span>Recent</span>
          </div>
        </div>
      )}

      <div className="bg-[#1e2a45]/30 rounded-xl p-4 border border-[#1e2a45]">
        <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
          <Globe className="h-4 w-4 text-cyan-400" />
          Predictions by Source
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(bySource).map(([source, count]) => (
            <div key={source} className="text-center p-3 bg-[#050810] rounded-lg">
              <div className={`w-3 h-3 rounded-full ${SOURCE_COLORS[source] || 'bg-gray-500'} mx-auto mb-2`} />
              <p className="text-white font-semibold">{count}</p>
              <p className="text-xs text-gray-500 capitalize">{source.replace('_', ' ')}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center gap-2">
          <Clock className="h-3 w-3" />
          <span>
            Last scrape: {scraperStatus?.last_scrape ? new Date(scraperStatus.last_scrape).toLocaleString() : 'Never'}
          </span>
        </div>
        {scraperStatus?.recent_errors?.length > 0 && (
          <div className="flex items-center gap-1 text-amber-400">
            <AlertCircle className="h-3 w-3" />
            <span>{scraperStatus.recent_errors.length} recent error(s)</span>
          </div>
        )}
      </div>

      {scraperStatus?.recent_errors?.length > 0 && (
        <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
          <h4 className="text-sm font-medium text-red-400 mb-2 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Recent Scraper Errors
          </h4>
          <div className="space-y-1">
            {scraperStatus.recent_errors.slice(0, 3).map((error, idx) => (
              <p key={`${error.source}-${error.timestamp || idx}`} className="text-xs text-red-300">
                <span className="text-red-400 font-medium">[{error.source}]</span> {error.error}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScraperHealthWidget;
